import * as _ from 'underscore';
import { SelectClickable } from './selectClickable';
import { Event, Util } from '..';
import { ISelectedEvent } from '../../events';
import { IEvent } from '../../interfaces';
import { MultiSelectClickableCollectionOptions } from '../../interfaces/options';
import { DefaultMultiSelectClickableCollectionOptions } from '../../const/defaultOptions';
import { Localization } from '../../compile';

export abstract class MultiSelectClickableCollection<
	T extends SelectClickable
> extends SelectClickable {
	private clickReload: () => void;
	private clickNew: () => void;
	private clickEdit: (item: T) => void;
	private clickDelete: (item: T) => void;
	private clickSelectedDelete: () => void;
	public isLoading: KnockoutObservable<boolean>;
	public items: KnockoutObservableArray<T>;
	private selectedItems: string[];
	private subscriptions: KnockoutSubscription[];
	protected deleteLabelExtension: KnockoutObservable<string>;
	private idField: string;
	public hasItems: KnockoutComputed<boolean>;

	abstract pull(params?: any): Promise<any>;
	abstract new(): void;
	abstract edit(item: T): void;
	abstract delete(id: string): Promise<any>;

	private onItemSelected: Event<ISelectedEvent>;
	public get ItemSelected(): IEvent<ISelectedEvent> {
		return this.onItemSelected;
	}

	public deleteLabel: KnockoutComputed<string>;

	constructor(options: MultiSelectClickableCollectionOptions<T>) {
		_.defaults(options, DefaultMultiSelectClickableCollectionOptions);
		options.callBack = () => {
			this.selectAllToggle();
		};
		super(options);

		this.initMultiSelect(options);
	}

	public initMultiSelect(options: MultiSelectClickableCollectionOptions<T>) {
		this.items = ko.isObservable(options.items)
			? <KnockoutObservableArray<T>>options.items
			: ko.observableArray(<T[]>options.items);
		this.selectedItems = [];
		this.subscriptions = [];
		this.idField = options.idField;
		this.hasItems = ko.pureComputed(() => {
			return this.items().length > 0;
		});

		this.bind();

		this.isLoading = ko.observable(false);
		this.clickReload = () => {
			this.reload();
		};
		this.clickNew = () => {
			this.new();
		};
		this.clickEdit = (item: T) => {
			this.edit(item);
		};
		this.clickDelete = (item: T) => {
			this.isLoading(true);
			this.delete(item.id)
				.then(() => {}, () => {})
				.then(this.reload);
		};
		this.clickSelectedDelete = () => {
			this.isLoading(true);
			this.deleteSelected()
				.then(() => {}, () => {})
				.then(this.reload);
		};
		this.onItemSelected = new Event<ISelectedEvent>();

		this.deleteLabelExtension = ko.observable<string>('');
		this.deleteLabel = ko.computed<string>({
			read: () => {
				return Localization.Delete.concat(' ', this.deleteLabelExtension());
			},
			write: (deleteLabelExtension: string) => {
				this.deleteLabelExtension(deleteLabelExtension);
			}
		});

		this.onItemSelected.subscribe(selectedEvent => {
			if (this.isSelected()) {
				this.deleteLabel(Localization.All);
			} else {
				var selectionCount = this.getSelectionCount();
				if (selectionCount > 0) {
					this.deleteLabel(`(${selectionCount})`);
				} else {
					this.deleteLabel('');
				}
			}
		});
	}

	private bind() {
		this.items().forEach(item => {
			if (item.isSelected()) {
				this.selectedItems.push(item.id);
			}

			this.subscribe(item);
		});
		this.isAllSelectedValidate();
	}

	private subscribeAll() {
		this.items().forEach(item => this.subscribe(item));
	}

	private subscribe(item: T) {
		this.subscriptions.push(
			item.isSelected.subscribe(isSelected => {
				if (isSelected) {
					this.selectedItems.push(item.id);
				} else {
					Util.remove(this.selectedItems, item.id);
				}
				this.isAllSelectedValidate();
				this.onItemSelected.notify(<ISelectedEvent>{
					id: item.id,
					isSelected: isSelected
				});
			})
		);
	}

	private unsubscribeAll() {
		this.subscriptions.forEach(subscription => subscription.dispose());
		this.subscriptions = [];
	}

	private isAllSelectedValidate() {
		var atLeastOneUnselected = _.any(this.items(), item => {
			return !item.isSelected();
		});
		this.bubbleAllChildSelected(!atLeastOneUnselected);
	}

	public bubbleAllChildSelected(selection: boolean) {
		this.isSelected(selection);
	}

	//private deleteSelected(): JQueryPromise<any> {
	//    if (this.isSelected())
	//        return this.deleteAll();

	//    return this.deleteItems(this.selectedItems);
	//}

	//public deleteAll(): JQueryPromise<any> {
	//    return this.deleteItems(_.map(this.items(), item => item.id));
	//}

	//private deleteItems(items: string[]): JQueryPromise<any> {
	//    var deferred = $.Deferred();

	//    var deleteDeferreds: JQueryPromise<any>[] = [];
	//    items.forEach(item => deleteDeferreds.push(this.delete(item)));

	//    $.when.apply($, deleteDeferreds).done(() => {
	//        deferred.resolve();
	//    });

	//    return deferred;
	//}

	// TODO: Revise
	// Replaced $.deferred with es6 Promise

	private deleteSelected(): Promise<any> {
		if (this.isSelected()) return this.deleteAll();

		return this.deleteItems(this.selectedItems);
	}

	public deleteAll(): Promise<any> {
		return this.deleteItems(_.map(this.items(), item => item.id));
	}

	private deleteItems(items: string[]): Promise<any> {
		return new Promise<void>(resolve => {
			var deleteDeferreds: Promise<any>[] = [];
			items.forEach(item => deleteDeferreds.push(this.delete(item)));

			$.when.apply($, deleteDeferreds).done(() => {
				resolve();
			});
		});
	}

	public selectAllToggle() {
		this.selectAll(this.isSelected());
	}

	public selectAll(isAllSelected: boolean) {
		this.unsubscribeAll();
		this.items().forEach(item => item.set(isAllSelected));
		if (isAllSelected) {
			this.selectedItems = _.pluck(this.items(), this.idField);
			this.deleteLabel(Localization.All);
		} else {
			this.selectedItems = [];
			this.deleteLabel('');
		}
		this.isSelected(isAllSelected);
		this.subscribeAll();
	}

	public setItems(items: T[]) {
		this.selectedItems = [];
		this.items(items);
		this.bind();
	}

	public getSelectionCount(): number {
		return this.selectedItems.length;
	}

	public reload(params?: any): Promise<any> {
		// ?
		//var deferred = $.Deferred<any>();
		//this.isLoading(true);
		//this.deleteLabel('');
		//this.pull(params).always((data) => {
		//    this.isLoading(false);
		//    deferred.reject(data);
		//});
		//return deferred.promise();
		// /?

		var deferred = $.Deferred<any>();
		this.isLoading(true);
		this.deleteLabel('');
		return this.pull(params)
			.then(() => {}, () => {})
			.then(data => {
				this.isLoading(false);
			});
	}

	public getSelectedItems(): T[] {
		var selectedItems = <T[]>[];
		this.items().forEach(item => {
			if (item.isSelected()) {
				selectedItems.push(item);
			}
		});
		return selectedItems;
	}

	public getSelectedIds(): string[] {
		return this.selectedItems;
	}

	public getItemsById(ids: string[]): T[] {
		var items = <T[]>[];
		this.items().forEach(item => {
			if (_.contains(ids, item.id)) {
				items.push(item);
			}
		});
		return items;
	}

	public clear() {
		this.unsubscribeAll();
		this.items().forEach(item => item.clear());
		this.items.removeAll();
		this.selectedItems = [];
		this.setSelected(false);
	}
}
